import { useState } from "react";
import { REMOVE_FOCUS_CSS } from "@components/Button/utils/constants";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { cn } from "@util/helpers";

interface FormElements extends HTMLFormControlsCollection {
  queryInput: HTMLInputElement;
}

interface KeywordsFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

type MyBTOnboardSearchInputProps = {
  /** The class  name for the input. */
  className?: string;
  /** The function to search the new list. */
  searchNewList: (value: string) => void;
  /** The ref for the input. */
  inputRef?: React.RefObject<HTMLInputElement>;
  /** The function to clear the search data. */
  handleClearSearchData: () => void;
  /** The function to handle the key down event. */
  handleKeyDown: () => void;
};

/**
 * This component is used to display the MyBT onboard search input.
 * It displays the search input, the search results, and the loading spinner.
 */
const MyBTOnboardSearchInput = ({
  className,
  searchNewList,
  handleClearSearchData,
  inputRef,
  handleKeyDown,
}: MyBTOnboardSearchInputProps): React.ReactElement => {
  const [withValue, setWithValue] = useState(false);

  const handleClearText = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.value = "";
      setWithValue(false);
      handleClearSearchData();
    }
  };

  const handleSubmit = (element: React.FormEvent<KeywordsFormElement>) => {
    element.preventDefault();
    searchNewList(element.currentTarget.elements.queryInput.value);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchNewList(event.target.value);

    if (event.target.value !== "" && !withValue) {
      setWithValue(true);
    }

    if (event.target.value === "" && withValue) {
      setWithValue(false);
    }
  };

  const handleKeyDownInput = () => {
    handleKeyDown();
  };

  return (
    <div
      className={cn(
        "group flex rounded-[4px] border border-gray-400 px-4 py-3 text-gray-515 transition-all md:w-[300px]",
        "focus-within:border-gray-850 focus-within:text-gray-850 md:focus-within:w-[450px]",
        "w-full",
        className,
        { "border-gray-850 text-gray-850 md:w-[450px]": withValue }
      )}
      data-testid="mybt-onboard-search-input-component"
    >
      <form
        autoComplete="off"
        className="flex w-full justify-between"
        onSubmit={handleSubmit}
      >
        <input
          className={cn(
            "w-full outline-0 placeholder:text-lg placeholder:font-normal placeholder:text-gray-550",
            REMOVE_FOCUS_CSS
          )}
          type="text"
          placeholder="Find more keywords"
          ref={inputRef}
          id="queryInput"
          onChange={(e) => {
            handleOnChange(e);
          }}
          onKeyDown={handleKeyDownInput}
        />

        {withValue ? (
          <button
            type="reset"
            onClick={handleClearText}
            className={cn(REMOVE_FOCUS_CSS)}
          >
            <IconFactory type={IconTypeEnum.close} />
          </button>
        ) : (
          <button type="submit" className={cn("pr-2", REMOVE_FOCUS_CSS)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect
                x="15.4141"
                y="14"
                width="5"
                height="1.8"
                transform="rotate(45 15.4141 14)"
                style={{ fill: "currentcolor" }}
              />

              <circle
                cx="11"
                cy="11"
                r="5.1"
                style={{ stroke: "currentcolor" }}
                strokeWidth="1.8"
              />
            </svg>
          </button>
        )}
      </form>
    </div>
  );
};

export default MyBTOnboardSearchInput;
